<template>
  <section
    class="fu-profile"
    data-test="fu-profile-section"
    :aria-labelledby="embedded ? null : 'fu-profile-title'"
    :aria-label="embedded ? 'Follow-ups' : null"
  >
    <div v-if="!embedded" class="fu-profile__head">
      <div class="fu-profile__titles">
        <h2 id="fu-profile-title" class="fu-profile__title">Follow-ups</h2>
        <span class="fu-profile__sub" data-test="fu-profile-counts">
          {{ openCount }} open · {{ pastDueCount }} past due — everything your team is chasing for this patient, across every queue.
        </span>
      </div>
      <div v-if="$slots.actions" class="fu-profile__actions">
        <slot name="actions" />
      </div>
    </div>

    <followups-queue
      mode="profile"
      queue="patientProfile"
      :rows="rows"
      :can-edit="canEdit"
      :profile-patient-id="profilePatientId"
      :patient-name="patientName"
      :profile-facts="profileFacts"
      :embedded="embedded"
      :max-rows="maxRows"
      @counts="onCounts"
    />
  </section>
</template>

<script>
/**
 * One patient's follow-ups across every queue (redesign Phase 5, 2026-10-02) — the export's
 * patient-profile view WITHOUT its patient card (the host already has one): the "Follow-ups"
 * heading, the "N open · M past due" line, and FollowupsQueue in mode="profile".
 *
 *   <followups-profile-section :rows="upcomingFollowups" :profile-patient-id="patientID"
 *     :patient-name="patientData.PatientName" :profile-facts="fuProfileFacts">
 *     <template #actions>…</template>
 *   </followups-profile-section>
 *
 * Mounted by Patient Profile today — as its Follow-ups tab, and `embedded` + `max-rows` as the first
 * rows of the Overview tab's Follow-ups card (same drawer and date popover in both); built to be
 * mounted as-is by the Schedule View Patient Panel's Follow-ups tab later. So it reads nothing from
 * the page or the route: rows (teamio.tasks rows in
 * the shape of main-service sql/patient-profile.js `upcomingFollowups`), the patient id and the
 * optional facts all come in as props. The rows are patched IN PLACE after a save or the drawer
 * (followupActions.js), so the host's own copy shows the change without a reload.
 *
 * "Open" = not stopped, done or resolved (the export: not stopped / done); "past due" = the
 * Follow-up cell says past due. Both from the queue's own `counts`, so they follow the same rules
 * as the cells, and a row that is saving keeps counting as it was.
 *
 * ⛔ Never mounts a <slideout-panel>: the drawer opens through the HOST page's single outlet.
 * ⛔ No props named patNum / siteID / loading (src/mixin.js shadows them in the queue).
 */
import FollowupsQueue from './FollowupsQueue.vue';

export default {
  name: 'FollowupsProfileSection',
  components: { FollowupsQueue },
  props: {
    rows: { type: Array, default: () => [] },
    profilePatientId: { type: String, default: null },
    patientName: { type: String, default: '' },
    profileFacts: { type: Object, default: null },
    // null ⇒ $can('update', 'followupDate'), as everywhere else.
    canEdit: { type: Boolean, default: null },
    // Inside a card the host already titles (Patient Profile's Overview): no heading / counts line
    // and no card border of the queue's own — just the rows.
    embedded: { type: Boolean, default: false },
    // Draw only the first N rows (0 = all); the counts still cover every row.
    maxRows: { type: Number, default: 0 },
  },
  data() {
    return { counts: null };
  },
  computed: {
    openCount() {
      const c = this.counts;
      return c ? Math.max(0, (c.all || 0) - (c.closed || 0)) : 0;
    },
    pastDueCount() {
      return this.counts ? this.counts.pastDue || 0 : 0;
    },
  },
  methods: {
    onCounts(c) {
      this.counts = c;
    },
  },
};
</script>

<style lang="scss" scoped>
@import './mixins';

.fu-profile {
  @include fu-font-base;
  min-width: 0;
}

.fu-profile__head {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 6px $fu-space-4;
  margin-bottom: 12px;
}

.fu-profile__titles {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px 12px;
  min-width: 0;
  flex: 1 1 260px;
}

.fu-profile__title {
  @include fu-font-display;
  margin: 0;
  font-weight: 800;
  font-size: 20px;
  letter-spacing: -0.02em;
  color: $fu-text;
}

.fu-profile__sub {
  font-size: 13px;
  line-height: 1.4;
  color: $fu-text-muted;
}

// Shrinks and wraps on a narrow column (the Super Admin line must never run off a phone screen).
.fu-profile__actions {
  display: flex;
  align-items: center;
  flex: 0 1 auto;
  min-width: 0;
  max-width: 100%;
}
</style>
